import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import {
  useMorningBriefSettings,
  useRunMorningBrief,
  useSaveMorningBriefSettings
} from '../../api/queries'
import type {
  MessageRecipient,
  MessageSettings,
  MorningBriefSettings,
  OfficeDay,
  OfficeHours,
  SendMessageResponse
} from '../../api/types'
import { useAuth } from '../../auth/AuthProvider'
import { Avatar, Card, ErrorBox, Loading } from '../../components/ui'
import { formatDateTime } from '../../lib/format'

/**
 * Beskeder til medarbejderne (beslutning 042).
 *
 * To ting på siden: den besked man sender her og nu, når noget haster, og -
 * for administratorer - hvilke af systemets automatiske beskeder der også skal
 * ud som SMS eller mail.
 *
 * Alt står som standard til fra. En SMS koster penge og forstyrrer folk uden
 * for skærmen, så det skal slås til bevidst.
 */
export function MessagesPage() {
  const { isAdmin } = useAuth()

  return (
    <div className="stack" style={{ gap: 16 }}>
      <SendCard />
      {isAdmin && <MorningBriefCard />}
      {isAdmin && <OfficeHoursCard />}
      {isAdmin && <RulesCard />}
    </div>
  )
}

/**
 * Morgenbeskeden med dagens plan (beslutning 081).
 *
 * Står her og ikke under Deadline-varsler, fordi det er en besked til folk og
 * ikke et varsel om en frist - og fordi SMS-fluebenet til den står lige nedenfor.
 */
function MorningBriefCard() {
  const settings = useMorningBriefSettings()
  const save = useSaveMorningBriefSettings()
  const run = useRunMorningBrief()

  const [form, setForm] = useState<MorningBriefSettings | null>(null)

  useEffect(() => {
    if (settings.data) setForm(settings.data)
  }, [settings.data])

  if (settings.error) return <ErrorBox error={settings.error} />
  if (!form) return null

  return (
    <Card title="Morgenbesked med dagens plan">
      <p className="small muted" style={{ marginTop: 0 }}>
        Én besked om morgenen med det, man skal i dag: hvor man skal hen, hvilke opgaver der falder på
        dagen, og hvad der er næste handling. <strong>Den, der ingenting har i kalenderen, får ingen
        besked</strong> - en tom besked hver morgen lærer folk at ignorere den.
      </p>

      <div className="stack" style={{ gap: 14 }}>
        <label className="row" style={{ gap: 8 }}>
          {/* width: auto - ellers fylder feltet hele bredden og skubber teksten væk. */}
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          />
          <span>Send morgenbeskeden automatisk</span>
        </label>

        <label className="stack" style={{ gap: 4 }}>
          <span className="small">Send kl.</span>
          <select
            value={form.runHour}
            onChange={(e) => setForm({ ...form, runHour: Number(e.target.value) })}
            style={{ maxWidth: 120 }}
          >
            {Array.from({ length: 24 }, (_, hour) => hour).map((hour) => (
              <option key={hour} value={hour}>
                {String(hour).padStart(2, '0')}:00
              </option>
            ))}
          </select>
          <span className="tiny muted">
            Dansk tid. Har serveren været nede på klokkeslættet, sendes beskeden ved første lejlighed
            bagefter - og aldrig to gange samme dag.
          </span>
        </label>
      </div>

      <div className="row" style={{ gap: 8, marginTop: 16 }}>
        <button className="btn btn--primary" disabled={save.isPending} onClick={() => save.mutate(form)}>
          {save.isPending ? 'Gemmer ...' : 'Gem'}
        </button>
        <button className="btn" disabled={run.isPending} onClick={() => run.mutate()}>
          {run.isPending ? 'Sender ...' : 'Send nu'}
        </button>
      </div>

      <p className="tiny muted" style={{ marginTop: 10 }}>
        Beskeden vises i appen og sendes som push. Skal den <strong>også</strong> ud som SMS - vejen til
        dem, der ikke har appen på telefonen - slås "Morgenbesked med dagens plan" til i tabellen nedenfor.
      </p>

      {save.isSuccess && !save.isPending && (
        <div className="small" style={{ marginTop: 6, color: 'var(--success)' }}>
          Opsætningen er gemt. Ændringen virker med det samme.
        </div>
      )}
      {save.error && <ErrorBox error={save.error} />}

      {run.data && (
        <div className="small" style={{ marginTop: 10 }}>
          {run.data.recipients === 0 ? (
            <>Ingen havde noget i kalenderen i dag, så der blev ikke sendt noget.</>
          ) : (
            <>
              Sendte dagens plan til {run.data.recipients}{' '}
              {run.data.recipients === 1 ? 'medarbejder' : 'medarbejdere'}. {run.data.skipped} havde
              ingenting i dag.
            </>
          )}
        </div>
      )}
      {run.error && <ErrorBox error={run.error} />}
    </Card>
  )
}

/** Den manuelle besked - det er den, der bruges ved et akut udkald. */
function SendCard() {
  const { data: recipients, isLoading, error } = useQuery({
    queryKey: ['message-recipients'],
    queryFn: () => api.get<MessageRecipient[]>('/api/messages/recipients')
  })

  const [selected, setSelected] = useState<number[]>([])
  const [message, setMessage] = useState('')
  const [subject, setSubject] = useState('')
  const [sms, setSms] = useState(true)
  const [mail, setMail] = useState(false)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<SendMessageResponse | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  const chosen = recipients?.filter((r) => selected.includes(r.id)) ?? []
  const utenNummer = sms ? chosen.filter((r) => !r.phoneNumber) : []

  function toggle(id: number) {
    setSelected((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]))
  }

  async function send() {
    setBusy(true)
    setProblem(null)
    setResult(null)
    try {
      const response = await api.post<SendMessageResponse>('/api/messages', {
        userIds: selected,
        message,
        subject: subject || null,
        sms,
        mail
      })
      setResult(response)
      setMessage('')
      setSubject('')
      setSelected([])
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Beskeden kunne ikke sendes.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="Send besked til medarbejdere">
      <p className="small muted" style={{ marginTop: 0 }}>
        Beskeden går afsted med det samme. Den er tænkt til det, der ikke kan vente til næste
        gang nogen åbner appen - et akut udkald.
      </p>

      <div className="field">
        <span className="field__label">
          Modtagere {selected.length > 0 && <span className="tiny muted">({selected.length} valgt)</span>}
        </span>
        <div className="cal-people">
          {recipients?.map((r) => (
            <label key={r.id} className={`cal-person ${selected.includes(r.id) ? 'cal-person--on' : ''}`}>
              <input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggle(r.id)} />
              <Avatar user={r} size={22} />
              <span>
                {r.displayName}
                {/* Uden nummeret her ville man vælge i blinde og først bagefter
                    opdage, at halvdelen ikke kan få en SMS. */}
                {!r.phoneNumber && <span className="tiny muted"> · intet nummer</span>}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="row" style={{ gap: 16, margin: '10px 0' }}>
        <label className="row tiny" style={{ gap: 6 }}>
          <input type="checkbox" checked={sms} onChange={(e) => setSms(e.target.checked)} />
          Send som SMS
        </label>
        <label className="row tiny" style={{ gap: 6 }}>
          <input type="checkbox" checked={mail} onChange={(e) => setMail(e.target.checked)} />
          Send som mail
        </label>
      </div>

      {mail && (
        <label className="field">
          <span className="field__label">Emne på mailen</span>
          <input maxLength={200} value={subject} onChange={(e) => setSubject(e.target.value)} />
        </label>
      )}

      <label className="field">
        <span className="field__label">Besked</span>
        <textarea rows={3} maxLength={1000} value={message} onChange={(e) => setMessage(e.target.value)} />
      </label>

      {utenNummer.length > 0 && (
        <div className="error-box" style={{ marginBottom: 10, background: '#FFF7ED', borderColor: '#FED7AA', color: '#9A3412' }}>
          {utenNummer.length === 1
            ? `${utenNummer[0].displayName} har intet telefonnummer og får ikke SMS'en.`
            : `${utenNummer.length} af de valgte har intet telefonnummer og får ikke SMS'en: ` +
              utenNummer.map((r) => r.displayName).join(', ')}
        </div>
      )}

      {problem && <div className="error-box" style={{ marginBottom: 10 }}>{problem}</div>}

      <button
        className="btn btn--primary"
        disabled={busy || selected.length === 0 || !message.trim() || (!sms && !mail)}
        onClick={() => void send()}
      >
        {busy ? 'Sender ...' : `Send til ${selected.length || 0}`}
      </button>

      {result && (
        <div className="small" style={{ marginTop: 12 }}>
          <div style={{ color: 'var(--success)' }}>
            Beskeden nåede {result.reached} af {result.results.length}.
          </div>
          {result.results.some((r) => r.reason) && (
            <ul className="tiny muted" style={{ paddingLeft: 18, marginTop: 6 }}>
              {result.results
                .filter((r) => r.reason)
                .map((r) => (
                  <li key={r.userId}>
                    {r.displayName}: {r.reason}
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  )
}

const DAY_NAMES = ['Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag', 'Søndag']

/**
 * Åbningstiden for beskeder (beslutning 138 og 140). Uden for den venter push, SMS og mail i kø, til der åbner -
 * undtagen beskedtyper med "Vigtig". Selve beskeden ses i appen med det samme. Kun Superadmin retter den.
 */
export function OfficeHoursCard() {
  const queryClient = useQueryClient()
  const { data } = useQuery({
    queryKey: ['message-settings'],
    queryFn: () => api.get<MessageSettings>('/api/messages/settings')
  })
  const [hours, setHours] = useState<OfficeHours | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

  useEffect(() => {
    if (data?.officeHours) setHours(data.officeHours)
  }, [data])

  if (!data || !hours) return null
  const canEdit = data.canEditOfficeHours

  function setDay(index: number, value: OfficeDay) {
    setHours((current) => current && { ...current, days: current.days.map((d, i) => (i === index ? value : d)) })
  }

  async function save() {
    setBusy(true)
    setMessage(null)
    try {
      await api.put('/api/messages/office-hours', hours)
      await queryClient.invalidateQueries({ queryKey: ['message-settings'] })
      setMessage({ ok: true, text: 'Åbningstiden er gemt.' })
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : 'Kunne ikke gemme.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="Åbningstid for beskeder">
      <p className="small muted" style={{ marginTop: 0 }}>
        Uden for åbningstiden venter beskederne i kø - push på telefonen, SMS og mail - og sendes, når der åbner.
        Beskedtyper med fluebenet <strong>Vigtig</strong> (herunder) sendes altid med det samme. Beskeden kan stadig ses
        inde i appen med det samme. Tiderne er dansk tid.
      </p>

      <div className="small" style={{ marginBottom: 10 }}>
        {!hours.enabled
          ? 'Slået fra - alt sendes med det samme.'
          : data.isOpenNow
            ? '🟢 Åbent nu - beskeder sendes.'
            : `🔴 Lukket nu${data.nextOpening ? ` - åbner ${formatDateTime(data.nextOpening)}` : ''}.`}
        {data.queuedCount > 0 && ` ${data.queuedCount} ${data.queuedCount === 1 ? 'besked venter' : 'beskeder venter'} i kø.`}
      </div>

      <label className="row small" style={{ gap: 8, marginBottom: 10 }}>
        <input
          type="checkbox"
          style={{ width: 'auto' }}
          disabled={!canEdit}
          checked={hours.enabled}
          onChange={(e) => setHours({ ...hours, enabled: e.target.checked })}
        />
        Send kun push, SMS og mail i åbningstiden
      </label>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Dag</th>
              <th style={{ width: 90 }}>Lukket</th>
              <th>Åbner</th>
              <th>Lukker</th>
            </tr>
          </thead>
          <tbody>
            {hours.days.map((day, i) => {
              const closed = !day.open && !day.close
              return (
                <tr key={DAY_NAMES[i]}>
                  <td>{DAY_NAMES[i]}</td>
                  <td>
                    <input
                      type="checkbox"
                      disabled={!canEdit}
                      checked={closed}
                      onChange={(e) => setDay(i, e.target.checked ? { open: null, close: null } : { open: '07:00', close: '15:15' })}
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      style={{ maxWidth: 130 }}
                      disabled={!canEdit || closed}
                      value={day.open ?? ''}
                      onChange={(e) => setDay(i, { ...day, open: e.target.value || null })}
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      style={{ maxWidth: 130 }}
                      disabled={!canEdit || closed}
                      value={day.close ?? ''}
                      onChange={(e) => setDay(i, { ...day, close: e.target.value || null })}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {message && (
        <div className={message.ok ? 'small' : 'error-box'} style={{ marginTop: 10, color: message.ok ? 'var(--success)' : undefined }}>
          {message.text}
        </div>
      )}

      {canEdit ? (
        <button className="btn btn--primary btn--sm" style={{ marginTop: 12 }} disabled={busy} onClick={() => void save()}>
          Gem åbningstid
        </button>
      ) : (
        <p className="tiny muted" style={{ marginTop: 10 }}>Kun Superadmin kan ændre åbningstiden og fluebenet Vigtig.</p>
      )}
    </Card>
  )
}

/** Hvilke af systemets egne beskeder der også går ud som SMS og mail. */
function RulesCard() {
  const queryClient = useQueryClient()
  const { data } = useQuery({
    queryKey: ['message-settings'],
    queryFn: () => api.get<MessageSettings>('/api/messages/settings')
  })

  const [kinds, setKinds] = useState<MessageSettings['kinds']>([])
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

  useEffect(() => {
    if (data) setKinds(data.kinds)
  }, [data])

  if (!data) return null

  function set(type: string, channel: 'sms' | 'mail' | 'important', value: boolean) {
    setKinds((current) => current.map((k) => (k.type === type ? { ...k, [channel]: value } : k)))
  }

  async function save() {
    setBusy(true)
    setMessage(null)
    try {
      await api.put('/api/messages/settings', { kinds })
      await queryClient.invalidateQueries({ queryKey: ['message-settings'] })
      setMessage({ ok: true, text: 'Gemt.' })
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : 'Kunne ikke gemme.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="Automatiske beskeder">
      <p className="small muted" style={{ marginTop: 0 }}>
        Systemets egne beskeder vises altid i appen og sendes som push til dem, der har slået det
        til på telefonen. Herunder vælger du, hvilke af dem der <strong>også</strong> skal ud som
        SMS eller mail. Alt står som standard til fra - en SMS koster penge og forstyrrer folk
        uden for skærmen.
      </p>

      {(!data.smsReady || !data.mailReady) && (
        <div className="error-box" style={{ marginBottom: 12, background: '#FFF7ED', borderColor: '#FED7AA', color: '#9A3412' }}>
          {!data.smsReady && 'SMS er ikke sat op eller ikke slået til under SMS. '}
          {!data.mailReady && 'Mail er ikke sat op eller ikke slået til under Microsoft 365 → Mail. '}
          Fluebenene herunder gør ingenting, før det er på plads.
        </div>
      )}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Besked</th>
              <th style={{ width: 70 }}>SMS</th>
              <th style={{ width: 70 }}>Mail</th>
              <th style={{ width: 90 }} title="Sendes med det samme - også uden for åbningstiden. Kun Superadmin.">Vigtig</th>
            </tr>
          </thead>
          <tbody>
            {kinds.map((k) => (
              <tr key={k.type}>
                <td>
                  <div>{k.name}</div>
                  <div className="tiny muted">{k.description}</div>
                </td>
                <td>
                  <input type="checkbox" checked={k.sms} onChange={(e) => set(k.type, 'sms', e.target.checked)} />
                </td>
                <td>
                  <input type="checkbox" checked={k.mail} onChange={(e) => set(k.type, 'mail', e.target.checked)} />
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={k.important}
                    disabled={!data.canEditOfficeHours}
                    title={data.canEditOfficeHours ? 'Send med det samme - også uden for åbningstiden' : 'Kun Superadmin kan ændre det'}
                    onChange={(e) => set(k.type, 'important', e.target.checked)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.smsSenderName && (
        <p className="tiny muted" style={{ marginTop: 10 }}>
          Hver SMS begynder med "{data.smsSenderName}:". Modtageren ser stadig gatewayens
          telefonnummer som afsender - et navn som afsender kræver en SMS-udbyder og kan ikke
          sættes herfra.
        </p>
      )}

      {message && (
        <div
          className={message.ok ? 'small' : 'error-box'}
          style={{ marginTop: 10, color: message.ok ? 'var(--success)' : undefined }}
        >
          {message.text}
        </div>
      )}

      <button className="btn btn--primary btn--sm" style={{ marginTop: 12 }} disabled={busy} onClick={() => void save()}>
        Gem
      </button>
    </Card>
  )
}
